'use client';

import {
  type ReactNode,
  type MutableRefObject,
  createRef,
  useMemo,
} from 'react';

import { Skeleton, Stack } from '@backyard-ui/core';
import { useMediaQuery } from '@backyard-ui/hooks';
import * as NavigationMenu from '@radix-ui/react-navigation-menu';

import { algolia } from '@/infra/tracking/algolia';

import { styles } from './DetailsNavigation.styles';
import useDetailsNavigation from './useDetailsNavigation';

type Ref = MutableRefObject<HTMLDivElement | null>;

type ExternalItem = {
  content: ReactNode;
  title: string;
  disabled?: boolean;
  isFetching?: boolean;
};

type Item = ExternalItem & { ref: Ref; key: string };

export type Items = Item[];

export type ExternalItems = Record<string, ExternalItem>;

interface DetailsNavigationProps {
  items: ExternalItems;
}

export default function DetailsNavigation(
  props: Readonly<DetailsNavigationProps>
) {
  const { items: externalItems } = props;

  const items = useMemo(
    () =>
      Object.keys(externalItems).reduce<Items>((acc, key) => {
        const externalItem = externalItems[key];

        if (externalItem && !externalItem.disabled) {
          const { isFetching } = externalItem;

          acc.push({
            ...externalItem,
            ref: createRef(),
            key,
            isFetching: isFetching ?? false,
            /**
             * Our current version of tailwind-variants can't treat undefined as false yet,
             * so we're redefining this here.
             *
             * Ref: https://github.com/heroui-inc/tailwind-variants/pull/146
             */
          });
        }

        return acc;
      }, []),
    [externalItems]
  );

  const { activeKey } = useDetailsNavigation({
    items,
  });

  const isDesktop = useMediaQuery('md');
  const classNames = styles();

  if (!items.length) {
    return null;
  }

  return (
    <div>
      {isDesktop && (
        <NavigationMenu.Root className={classNames.navigationRoot()}>
          <NavigationMenu.List className={classNames.navigationList()}>
            {items.map(({ key, title, isFetching }) => (
              <NavigationMenu.Item key={key}>
                <NavigationMenu.Link
                  className={classNames.navigationItem({
                    isActive: activeKey === key,
                    isFetching,
                  })}
                  href={`#${key}`}
                  onClick={() => {
                    algolia.sendPdpClickEvent();
                  }}
                >
                  {isFetching ? (
                    <Skeleton
                      data-testid="skeleton"
                      height="16px"
                      width="80px"
                    />
                  ) : (
                    title
                  )}
                </NavigationMenu.Link>
              </NavigationMenu.Item>
            ))}
          </NavigationMenu.List>
        </NavigationMenu.Root>
      )}

      <Stack direction="column" spacing="4">
        {items.map(({ content, key, ref }) => (
          <div
            key={key}
            ref={ref}
            id={key}
            className="scroll-mt-12 bg-white p-6"
          >
            {content}
          </div>
        ))}
      </Stack>
    </div>
  );
}
